// @ts-nocheck
import { type SchoolConfigData } from '@jo-v1/components/ExitStrategy/type';
import Autolinker from 'autolinker';
import { type FC, useEffect, useMemo, useState } from 'react';
import { HiOutlineClock, HiOutlineClipboardList } from 'react-icons/hi';
import { useSelector } from 'react-redux';

import useElementEvents from '@core/hooks/cohesion/useElementEvents';
import useDualCtaTest from '@core/hooks/useDualCtaTest';
import useProgramCode from '@core/hooks/useProgramCode';
import useSchoolConfig from '@core/hooks/useSchoolConfig';
import {
  selectHeclidMap,
  selectViewCorrelationIdMap,
  selectProductCorrelationIdMap,
} from '@core/reducers/eventingSlice';
import { selectInput } from '@core/reducers/inputsSlice';
import { selectSubmissions } from '@core/reducers/matchesSlice';
import { mobiusGetApplicationCta } from '@core/services/mobius';
import appendQueryParams from '@core/utils/appendQueryParams';
import cn from '@core/utils/classNames';
import { applicationCtaBtn, continueBrowsingBtn } from '@core/utils/objects/eventPayloads';

import styles from './RecommenderExitStrategy.module.css';

// Reuse for different locations (mobile vs. desktop)
const SubmitButton = ({ applicationCtaData, elementClicked }) => {
  const referenceIdQueryParams = useSelector((state) => state?.syncLead?.referenceIdQueryParam);
  const handleApplication = (e, url) => {
    e.preventDefault();
    elementClicked({ ...applicationCtaBtn, outboundUrl: applicationCtaData?.url });
    const finalUrl = appendQueryParams(url, referenceIdQueryParams);
    window.open(finalUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      {applicationCtaData?.url && (
        <button
          className={styles.applicationCta}
          type="button"
          onClick={(e) => handleApplication(e, applicationCtaData?.url)}
        >
          Start Your Application
        </button>
      )}
    </>
  );
};

type Props = {
  closePopup?: () => void;
  eventingOverrides: Record<string, any>;
};

const RecommenderExitStrategy: FC<Props> = ({ closePopup, eventingOverrides }) => {
  // Store
  const firstName = useSelector((state) => selectInput(state, 'firstName'));
  const submissions = useSelector(selectSubmissions);
  const heclidMap = useSelector(selectHeclidMap);
  const productCorrelationIdMap = useSelector(selectProductCorrelationIdMap);
  const viewCorrelationIdMap = useSelector(selectViewCorrelationIdMap);
  const { program, school } = submissions[0];
  const schoolId = school?.id?.toString() || '';

  const { data }: SchoolConfigData = useSchoolConfig(school.id);

  const introText =
    data?.confirmation?.meta.shortIntro ||
    data?.confirmation?.meta.text ||
    'An Admissions Advisor will be in touch soon.';

  const autolinkedIntroText = Autolinker.link(introText);

  // Local state
  const [isIntroTextExpanded, setIsIntroTextExpanded] = useState(false);
  const [applicationCtaData, setApplicationCtaData] = useState(null);
  const showViewMoreBtn = autolinkedIntroText?.length > 110;

  const { elementViewed, elementClicked } = useElementEvents({
    correlationId: viewCorrelationIdMap[program.id],
    webElement: {
      location: eventingOverrides?.location,
    },
  });

  useEffect(() => {
    elementViewed(continueBrowsingBtn);

    const getApplicationCtaData = async () => {
      const response = await mobiusGetApplicationCta({
        programId: `${program.id}`,
        heclid: heclidMap[program.id],
        correlationId: productCorrelationIdMap[program.id],
      });
      setApplicationCtaData({ label: response?.label, url: response?.url });
    };

    getApplicationCtaData();
  }, []);

  useEffect(() => {
    if (applicationCtaData?.url) {
      elementViewed(applicationCtaBtn);
    }
  }, [applicationCtaData]);

  const programCode = useProgramCode(program?.id);
  const { DualCtaTestComponent, shouldApplyWrapperStyles } = useDualCtaTest(schoolId, programCode);

  return (
    <div className={styles.RecommenderExitStrategy}>
      <div className={styles.congrats}>
        <h2>Congratulations{firstName ? `, ${firstName}` : ''}!</h2>
        <p>Your information was submitted successfully.</p>
      </div>

      <p className={styles.noDate}>
        <span className={styles.clockIcon}>
          <HiOutlineClock size={20} />
        </span>
        Now accepting new students.
      </p>
      <div className={styles.applyOnline}>
        <h3>
          <span className={styles.clipboardIcon}>
            <HiOutlineClipboardList size={20} />
          </span>
          Apply Online Today
        </h3>
        <p>Online applications are fast, convenient, and let you track your acceptance status. Start yours today!</p>
      </div>
      <div className={shouldApplyWrapperStyles ? styles.dualCta : styles.singleCta}>
        <SubmitButton elementClicked={elementClicked} applicationCtaData={applicationCtaData} />
        <DualCtaTestComponent schoolId={schoolId} />
      </div>

      <section className={styles.exitStrategyContentContainer}>
        <div>
          <h2>What to expect from your application:</h2>
          <ul>
            <li>Most applications don&apos;t take long to complete.</li>
            <li>
              Be prepared to highlight your strengths, achievements, and individuality to show the school who you are.
            </li>
            <li>Depending on the school and degree level, you may be asked to provide your GPA and test scores.</li>
            <li>After submitting your application, track its status online. The school should have a decision soon.</li>
          </ul>
        </div>
        <div>
          <h2 className={styles.secondTitle}>Preparing for a chat with your advisor:</h2>
          <ul>
            <li>
              Generally, you can expect to be called immediately or within 2 business days, depending on the
              availability of the school&apos;s counselors. If you miss your call, {school?.name} will try again.
            </li>
            <li>Calls average 20-30 minutes, and you&apos;ll typically receive a follow-up email afterwards.</li>
            <li>Be prepared to talk about your work and school history.</li>
            <li>Write down a few questions before the call</li>
          </ul>
        </div>
        <div>
          <h2 className={styles.secondTitle}>Here&apos;s what other students are asking:</h2>
          <ul>
            <li>What type of courses will I be taking for my degree?</li>
            <li>What scholarships and financial aid are available?</li>
            <li>How can I estimate my tuition cost, including books and fees?</li>
          </ul>
        </div>
      </section>
    </div>
  );
};

export default RecommenderExitStrategy;
